import { useAppDispatch, useAppSelector } from "app/hooks"
import { useEffect } from "react"
import {
  getPlans,
  getUserPlan,
  setRemainingLogMealTimeToShowPaywall,
} from "../paymentSlice"
import { useFailed } from "features/notification/hooks"
import { REMAINING_LOG_MEAL_TIME_TO_SHOW_PAYWALL } from "config"
import {
  checkHasAIEntitlement,
  checkHasScanBarcodeScanningEntitlement,
  checkIsBasePlan,
} from "utils"
import { isInWebView } from "utils/webViewDetection"

export const usePlans = () => {
  const getPlansLoading = useAppSelector(
    (state) => state.payment.getPlansLoading,
  )
  const plans = useAppSelector((state) => state.payment.plans)
  const getPlansFailed = useAppSelector((state) => state.payment.getPlansFailed)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!plans.length) {
      dispatch(getPlans())
    }
  }, [])

  useFailed(getPlansFailed)

  return {
    getPlansLoading,
    plans,
  }
}

export const useUserPlan = () => {
  const getUserPlanLoading = useAppSelector(
    (state) => state.payment.getUserPlanLoading,
  )
  const userPlan = useAppSelector((state) => state.payment.userPlan)
  const getUserPlanFailed = useAppSelector(
    (state) => state.payment.getUserPlanFailed,
  )

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getUserPlan())
  }, [])

  useFailed(getUserPlanFailed)

  return {
    getUserPlanLoading,
    userPlan,
  }
}

export const usePaywall = () => {
  const { userPlan } = useUserPlan()

  const isBasePlan = checkIsBasePlan(userPlan?.type ?? "")

  const remainingLogMealTimeToShowPaywall = useAppSelector(
    (state) => state.payment.remainingLogMealTimeToShowPaywall,
  )

  const dispatch = useAppDispatch()
  const onDecreaseRemainingLogMealTimeToShowPaywall = () => {
    dispatch(
      setRemainingLogMealTimeToShowPaywall(
        remainingLogMealTimeToShowPaywall - 1,
      ),
    )
  }

  const hasAIScanEntitlement = checkHasAIEntitlement(
    userPlan?.entitlements ?? [],
  )

  const hasBarcodeScanningEntitlement = checkHasScanBarcodeScanningEntitlement(
    userPlan?.entitlements ?? [],
  )

  const checkToShowPaywallWhenOpeningActions = () => {
    if (hasAIScanEntitlement) {
      return false
    } else {
      if (remainingLogMealTimeToShowPaywall <= 0) {
        dispatch(
          setRemainingLogMealTimeToShowPaywall(
            REMAINING_LOG_MEAL_TIME_TO_SHOW_PAYWALL,
          ),
        )
        return true
      } else {
        onDecreaseRemainingLogMealTimeToShowPaywall()
        return false
      }
    }
  }

  const shouldShowPaywallInWebView =
    !hasAIScanEntitlement &&
    remainingLogMealTimeToShowPaywall <= 0 &&
    isInWebView()

  return {
    checkToShowPaywallWhenOpeningActions,
    shouldShowPaywallInWebView,
    isBasePlan,
    hasAIScanEntitlement,
    hasBarcodeScanningEntitlement,
  }
}

export const useRefreshFJUserPlan = () => {
  const dispatch = useAppDispatch()
  const token = useAppSelector((state) => state.auth.token)

  useEffect(() => {
    const refreshUserPlan = () => {
      if (token) {
        dispatch(getUserPlan())
      }
    }

    if (isInWebView()) {
      ;(window as any).refreshFJUserPlan = refreshUserPlan
    }
  }, [token])
}
